import { zodResolver } from '@hookform/resolvers/zod'
import { Edit, CreditCard } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

// Componentes UI Globales
import { FormError } from '../components/ui/FormError'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Input } from '../components/ui/input'
import { TableSkeleton } from '../components/ui/table-skeleton'
// Servicios
import {
  paymentMethodService,
  PaymentMethodItem,
} from '../services/payment-method.service'

// ─── 1. Esquema de Validación (Zod) ──────────────────────────────────────────
const formSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(255, 'Demasiado largo'),
  code: z
    .string()
    .min(1, 'El código es obligatorio')
    .max(50, 'Máximo 50 caracteres')
    .regex(/^[a-z0-9_]+$/, 'Solo minúsculas, números y guiones bajos (_)'),
  is_active: z.boolean(),
  sort_order: z.number().int().min(0, 'El orden no puede ser negativo').max(65535),
})

type FormValues = z.infer<typeof formSchema>

type PaginationMeta = {
  total: number
  from: number | null
  to: number | null
}

// ─── 2. Componente Principal ───────────────────────────────────────────────
export function PaymentMethods() {
  // ─── Estados ───
  const [data, setData] = useState<PaymentMethodItem[]>([])
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
    total: 0,
    from: 0,
    to: 0,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshCount, setRefreshCount] = useState(0)

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // ─── Formulario (React Hook Form) ───
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      code: '',
      is_active: true,
      sort_order: 0,
    },
  })

  const isActiveValue = watch('is_active')

  // ─── Cargar Datos de la API ───
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await paymentMethodService.getItems({
          search: searchQuery,
          page: currentPage,
          per_page: itemsPerPage,
        })

        const items = response.data?.data || response.data || []
        setData(items)
        setPaginationMeta(response.data || response.meta || { total: 0, from: 0, to: 0 })
      } catch {
        toast.error('Error al cargar los métodos de pago')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshCount, searchQuery, currentPage])

  const paginatedData = data

  // ─── Funciones de Interfaz ───
  const openEditModal = (item: PaymentMethodItem) => {
    setEditingId(item.id)
    reset({
      name: item.name,
      code: item.code,
      is_active: item.is_active,
      sort_order: item.sort_order,
    })
    setShowModal(true)
  }

  // ─── Funciones API ───
  const onSubmit = async (formData: FormValues) => {
    try {
      if (editingId) {
        await paymentMethodService.updateItem(editingId, {
          name: formData.name,
          is_active: formData.is_active,
          sort_order: formData.sort_order,
        })
        toast.success('Método de pago actualizado')
      } else {
        await paymentMethodService.createItem(formData)
        toast.success('Método de pago creado')
      }
      setShowModal(false)
      setRefreshCount((prev) => prev + 1)
    } catch {
      toast.error('Error al guardar el método de pago')
    }
  }

  // ─── Renderizado (JSX) ───────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6">
      {/* 1. Header y Buscador */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-header-foreground">Métodos de Pago</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona las formas de pago aceptadas en el punto de venta.
          </p>
        </div>
        {/* La creación de métodos de pago está deshabilitada. */}
      </div>
<GlobalFilterBar
  show={['search']}
  values={{
    search: searchQuery,
  }}
  onChange={(changes) => {
    if (changes.search !== undefined) {
      setSearchQuery(changes.search)
      setCurrentPage(1)
    }
  }}
  customOptions={{
    searchPlaceholder: 'Buscar método de pago...',
  }}
/>
      {/* 2. Tabla de Datos */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Código', 'Orden', 'Referencia', 'Cambio', 'Estado', 'Acciones']}
          columns={['text', 'text', 'text', 'badge', 'badge', 'badge', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr className="border-b border-border transition-colors hover:bg-table-row-hover">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Orden
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Referencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Cambio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <tr
                      key={item.id}
                      className="transition-colors hover:bg-table-row-hover"
                    >
                      <td className="px-6 py-4 font-medium text-table-foreground">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 text-table-foreground">
                        {item.code}
                      </td>
                      <td className="px-6 py-4 text-table-foreground">{item.sort_order}</td>
                      <td className="px-6 py-4">
                        <Badge className={item.requires_reference ? 'bg-slate-100 text-slate-600' : 'bg-slate-100 text-slate-600'}>
                          {item.requires_reference ? 'Sí' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={item.allows_change ? 'bg-slate-100 text-slate-600' : 'bg-slate-100 text-slate-600'}>
                          {item.allows_change ? 'Sí' : 'No'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={
                            item.is_active
                              ? 'bg-[#DCFCE6] text-[#008438]'
                              : 'bg-[#FFE2E2] text-[#C10008]'
                          }
                        >
                          {item.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(item)}
                             title="Editar"
                              className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <CreditCard className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron métodos de pago</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 3. Paginación */}
          {paginationMeta.total > 0 && (
            <GlobalPaginator
              totalItems={paginationMeta.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              from={paginationMeta.from ?? undefined}
              to={paginationMeta.to ?? undefined}
            />
          )}
        </div>
      )}

      {/* 4. Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Editar Método de Pago' : 'Nuevo Método de Pago'}
            </DialogTitle>
            <DialogDescription>
              Configura cómo se presenta y se captura este método durante una venta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
            {/* Ocultamos la edición del código por regla de negocio
            <div>
              <label htmlFor="code" className="mb-1 block text-sm font-medium text-slate-700">
                Código *
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Código <span className="text-red-500">*</span>
              </label>
              <Input
                id="code"
                {...register('code')}
                disabled={editingId !== null}
                placeholder="ej. efectivo, tarjeta_credito"
                className="font-mono text-sm bg-slate-100 text-slate-500 cursor-not-allowed"
                disabled
              />
              {errors.code && <FormError message={errors.code.message} />}
              <p className="mt-1 text-xs text-slate-500">
                Identificador único interno (solo minúsculas y guiones bajos).
              </p>
            </div>
            */}
            <input type="hidden" {...register('code')} />

            <div>
              <label className="mb-1 block text-sm font-medium text-card-foreground">
                Nombre <span className="text-red-500">*</span>
              </label>
              <Input id="name" {...register('name')} placeholder="Efectivo, Tarjeta de Crédito" />
              {errors.name && <FormError message={errors.name.message} />}
            </div>

            <div>
              <label htmlFor="sort_order" className="mb-1 block text-sm font-medium text-card-foreground">
                Orden de aparición
              </label>
              <Input
                id="sort_order"
                type="number"
                min={0}
                max={65535}
                {...register('sort_order', { valueAsNumber: true })}
              />
              {errors.sort_order && <FormError message={errors.sort_order.message} />}
              <p className="mt-1 text-xs  text-card-description">
                Los números menores aparecen primero.
              </p>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <Checkbox
                id="is_active"
                checked={isActiveValue}
                onCheckedChange={(value) =>
                  setValue('is_active', value === true, { shouldValidate: true })
                }
              />
              <label htmlFor="is_active" className="text-sm font-medium text-card-foreground">
                Estado Activo
              </label>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
